import { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { money, fmtDDMMYYYY } from '../lib/format';
import { Loader } from '../ui/common';
import { IconChart } from '../clientModule/components/SvgIcons';

export default function ClientProfitAnalysis({ config, onOpenClient }) {
  const [clients, setClients] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('All');
  const [filterService, setFilterService] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All'); // 'All' | 'Active' | 'Inactive' | 'Profit' | 'Low Profit' | 'Loss'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('profit_desc');

  const currency = config?.currency || '₹';

  // Load all required dynamic data
  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/client-module/clients').catch(() => []),
      api.get('/api/income').catch(() => []),
      api.get('/api/expenses').catch(() => []),
      api.get('/api/client-module/services').catch(() => []),
    ])
      .then(([clientData, incomeData, expenseData, serviceData]) => {
        setClients(clientData || []);
        setIncomes(incomeData || []);
        setExpenses(expenseData || []);
        setServices(serviceData || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    window.addEventListener('syncSheet', loadData);
    return () => window.removeEventListener('syncSheet', loadData);
  }, []);

  // Filtered Raw Transactions by Date Range
  const filteredIncomes = useMemo(() => {
    return incomes.filter((inc) => {
      if (dateFrom && inc.date < dateFrom) return false;
      if (dateTo && inc.date > dateTo) return false;
      return true;
    });
  }, [incomes, dateFrom, dateTo]);

  const isPartnerSalary = (category) => {
    const cat = String(category || '').toLowerCase().trim();
    return cat.includes('partner') && (
      cat.includes('salary') ||
      cat.includes('selery') ||
      cat.includes('salery') ||
      cat.includes('draw') ||
      cat.includes('profit')
    );
  };

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => {
      if (isPartnerSalary(exp.category)) return false;
      if (dateFrom && exp.date < dateFrom) return false;
      if (dateTo && exp.date > dateTo) return false;
      return true;
    });
  }, [expenses, dateFrom, dateTo]);

  // Dynamic Client Profit Calculations
  const clientProfitData = useMemo(() => {
    return clients.map((c) => {
      const cNameLower = (c.clientName || '').toLowerCase().trim();
      const cCompanyLower = (c.company || '').toLowerCase().trim();

      // 1. Income matching this client (from Sheet Income transactions)
      const matchingSheetIncomes = filteredIncomes.filter((inc) => {
        const cat = (inc.category || '').toLowerCase();
        const desc = (inc.description || '').toLowerCase();
        return (cNameLower && (cat.includes(cNameLower) || desc.includes(cNameLower))) ||
               (cCompanyLower && (cat.includes(cCompanyLower) || desc.includes(cCompanyLower)));
      });

      const sheetIncomeTotal = matchingSheetIncomes.reduce((s, r) => s + (Number(r.net || r.amount) || 0), 0);

      // 2. Service Orders Income (if no direct sheet transactions, calculate from client serviceOrders)
      const orders = c.serviceOrders || [];
      const orderIncomeTotal = orders.reduce((s, o) => s + (Number(o.amountReceived || o.finalAmount) || 0), 0);

      // Total Income = Max of (Sheet Incomes, Service Orders Received) or sum if distinct
      const totalIncome = sheetIncomeTotal > 0 ? sheetIncomeTotal : orderIncomeTotal;

      // 3. Expenses matching this client (from Sheet Expenses transactions)
      const matchingSheetExpenses = filteredExpenses.filter((exp) => {
        const cat = (exp.category || '').toLowerCase();
        const desc = (exp.description || '').toLowerCase();
        const rem = (exp.remarks || '').toLowerCase();
        return (cNameLower && (cat.includes(cNameLower) || desc.includes(cNameLower) || rem.includes(cNameLower))) ||
               (cCompanyLower && (cat.includes(cCompanyLower) || desc.includes(cCompanyLower) || rem.includes(cCompanyLower)));
      });

      const totalExpense = matchingSheetExpenses.reduce((s, r) => s + (Number(r.net || r.amount) || 0), 0);

      // 4. Gross Profit & Profit Margin
      const grossProfit = totalIncome - totalExpense;
      const profitMarginPct = totalIncome > 0 ? (grossProfit / totalIncome) * 100 : grossProfit < 0 ? -100 : 0;

      // 5. Total Transactions Count
      const totalTxnCount = matchingSheetIncomes.length + matchingSheetExpenses.length + orders.length;

      // 6. Last Payment / Activity Date
      const allDates = [
        ...matchingSheetIncomes.map((i) => i.date),
        ...orders.map((o) => o.startDate || o.createdAt),
        c.joinDate,
      ].filter(Boolean).sort().reverse();
      const lastPaymentDate = allDates[0] || c.joinDate || '';

      // 7. Subscribed Service Names
      const serviceNames = orders.map((o) => o.serviceName).filter(Boolean);

      // 8. Health Badge Status
      let healthStatus = 'Profit'; // 🟢 Profit
      if (grossProfit < 0) {
        healthStatus = 'Loss'; // 🔴 Loss
      } else if (profitMarginPct < 25 && totalIncome > 0) {
        healthStatus = 'Low Profit'; // 🟡 Low Profit
      }

      return {
        id: c.id,
        clientName: c.clientName,
        company: c.company || c.clientName,
        status: c.status || 'Active',
        totalIncome,
        totalExpense,
        grossProfit,
        profitMarginPct,
        totalTxnCount,
        lastPaymentDate,
        serviceNames,
        healthStatus,
      };
    });
  }, [clients, filteredIncomes, filteredExpenses]);

  // Apply Search, Filters, and Sorting
  const filteredClients = useMemo(() => {
    return clientProfitData.filter((item) => {
      // Search term
      if (search) {
        const q = search.toLowerCase();
        const matchName = item.clientName.toLowerCase().includes(q);
        const matchComp = item.company.toLowerCase().includes(q);
        if (!matchName && !matchComp) return false;
      }

      // Client filter
      if (filterClient !== 'All' && item.id !== filterClient) return false;

      // Service filter
      if (filterService !== 'All' && !item.serviceNames.includes(filterService)) return false;

      // Status filter
      if (filterStatus !== 'All') {
        if (filterStatus === 'Active' || filterStatus === 'Inactive') {
          if (item.status !== filterStatus) return false;
        } else if (['Profit', 'Low Profit', 'Loss'].includes(filterStatus)) {
          if (item.healthStatus !== filterStatus) return false;
        }
      }

      return true;
    });
  }, [clientProfitData, search, filterClient, filterService, filterStatus]);

  // Sorted Clients
  const sortedClients = useMemo(() => {
    return [...filteredClients].sort((a, b) => {
      if (sortBy === 'profit_desc') return b.grossProfit - a.grossProfit;
      if (sortBy === 'profit_asc') return a.grossProfit - b.grossProfit;
      if (sortBy === 'margin_desc') return b.profitMarginPct - a.profitMarginPct;
      if (sortBy === 'margin_asc') return a.profitMarginPct - b.profitMarginPct;
      if (sortBy === 'income_desc') return b.totalIncome - a.totalIncome;
      if (sortBy === 'expense_desc') return b.totalExpense - a.totalExpense;
      if (sortBy === 'name') return a.clientName.localeCompare(b.clientName);
      return 0;
    });
  }, [filteredClients, sortBy]);

  // Dashboard Aggregates
  const totalRevenue = useMemo(() => sortedClients.reduce((s, c) => s + c.totalIncome, 0), [sortedClients]);
  const totalExpenses = useMemo(() => sortedClients.reduce((s, c) => s + c.totalExpense, 0), [sortedClients]);
  const totalGrossProfit = totalRevenue - totalExpenses;
  const avgProfitMargin = totalRevenue > 0 ? (totalGrossProfit / totalRevenue) * 100 : 0;

  const highestProfitClient = useMemo(() => {
    if (sortedClients.length === 0) return null;
    return [...sortedClients].sort((a, b) => b.grossProfit - a.grossProfit)[0];
  }, [sortedClients]);

  const lowestProfitClient = useMemo(() => {
    if (sortedClients.length === 0) return null;
    return [...sortedClients].sort((a, b) => a.grossProfit - b.grossProfit)[0];
  }, [sortedClients]);

  const lossClients = useMemo(() => {
    return sortedClients.filter((c) => c.grossProfit < 0);
  }, [sortedClients]);

  // CSV Export Handler
  const exportCSV = () => {
    const headers = [
      'Client Name', 'Company Name', 'Total Income (INR)', 'Total Expense (INR)',
      'Gross Profit (INR)', 'Profit Margin (%)', 'Total Transactions', 'Last Payment Date', 'Status', 'Health'
    ];
    const rows = sortedClients.map((c) => [
      `"${c.clientName.replace(/"/g, '""')}"`,
      `"${c.company.replace(/"/g, '""')}"`,
      c.totalIncome,
      c.totalExpense,
      c.grossProfit,
      `${c.profitMarginPct.toFixed(1)}%`,
      c.totalTxnCount,
      c.lastPaymentDate || '',
      c.status,
      c.healthStatus,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Client_Profit_Analysis_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF / Print Handler
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader />
        <div style={{ marginTop: 12, fontWeight: 700 }}>Computing Client Profit Analytics…</div>
      </div>
    );
  }

  return (
    <div className="client-profit-analysis-page">

      {/* ── Dashboard Summary Widgets ── */}
      <div className="cm-ref-stats-row" style={{ marginBottom: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        
        {/* Total Revenue */}
        <div className="cm-ref-stat-card" style={{ borderTop: '4px solid #224eae' }}>
          <div className="cm-ref-stat-label">TOTAL CLIENT REVENUE</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{money(totalRevenue, currency)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Across {sortedClients.length} clients</div>
        </div>

        {/* Total Expenses */}
        <div className="cm-ref-stat-card" style={{ borderTop: '4px solid #0f172a' }}>
          <div className="cm-ref-stat-label">TOTAL CLIENT EXPENSES</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--ink)' }}>{money(totalExpenses, currency)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Attributed expenses</div>
        </div>

        {/* Total Gross Profit */}
        <div className="cm-ref-stat-card" style={{ borderTop: `4px solid ${totalGrossProfit >= 0 ? '#224eae' : '#0f172a'}` }}>
          <div className="cm-ref-stat-label">TOTAL GROSS PROFIT</div>
          <div className="cm-ref-stat-val" style={{ color: totalGrossProfit >= 0 ? '#224eae' : '#0f172a' }}>
            {money(totalGrossProfit, currency)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Net profit after expenses</div>
        </div>

        {/* Average Profit Margin */}
        <div className="cm-ref-stat-card" style={{ borderTop: '4px solid #6366f1' }}>
          <div className="cm-ref-stat-label">AVG PROFIT MARGIN</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>
            {avgProfitMargin.toFixed(1)}%
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Overall profit efficiency</div>
        </div>

        {/* Highest Profit Client */}
        <div className="cm-ref-stat-card" style={{ borderTop: '4px solid #0f172a' }}>
          <div className="cm-ref-stat-label">HIGHEST PROFIT CLIENT</div>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--ink)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            🏆 {highestProfitClient ? highestProfitClient.clientName : '—'}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--brand)', fontWeight: 800, marginTop: 2 }}>
            {highestProfitClient ? money(highestProfitClient.grossProfit, currency) : '₹0'} ({highestProfitClient?.profitMarginPct.toFixed(0)}%)
          </div>
        </div>

        {/* Lowest Profit Client */}
        <div className="cm-ref-stat-card" style={{ borderTop: '4px solid #224eae' }}>
          <div className="cm-ref-stat-label">LOWEST PROFIT CLIENT</div>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--ink)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            🔻 {lowestProfitClient ? lowestProfitClient.clientName : '—'}
          </div>
          <div style={{ fontSize: '0.85rem', color: lowestProfitClient && lowestProfitClient.grossProfit < 0 ? '#0f172a' : '#224eae', fontWeight: 800, marginTop: 2 }}>
            {lowestProfitClient ? money(lowestProfitClient.grossProfit, currency) : '₹0'} ({lowestProfitClient?.profitMarginPct.toFixed(0)}%)
          </div>
        </div>

        {/* Loss Making Clients */}
        <div className="cm-ref-stat-card" style={{ borderTop: `4px solid ${lossClients.length > 0 ? '#0f172a' : '#224eae'}` }}>
          <div className="cm-ref-stat-label">LOSS MAKING CLIENTS</div>
          <div className="cm-ref-stat-val" style={{ color: lossClients.length > 0 ? '#0f172a' : '#224eae' }}>
            {lossClients.length}
          </div>
          <div style={{ fontSize: '0.75rem', color: lossClients.length > 0 ? '#0f172a' : '#64748b', marginTop: 4, fontWeight: 700 }}>
            {lossClients.length > 0 ? `⚠️ ${lossClients.map((l) => l.clientName).join(', ')}` : '✓ All clients profitable'}
          </div>
        </div>

      </div>

      {/* ── Toolbar & Filters ── */}
      <div className="cm-ref-toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
        
        {/* Search */}
        <input
          className="cm-ref-search"
          placeholder="Search client or company name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 220, flex: 1 }}
        />

        {/* Filter by Client */}
        <select className="cm-select-filter" value={filterClient} onChange={(e) => setFilterClient(e.target.value)}>
          <option value="All">All Clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.clientName}</option>
          ))}
        </select>

        {/* Filter by Service */}
        <select className="cm-select-filter" value={filterService} onChange={(e) => setFilterService(e.target.value)}>
          <option value="All">All Services</option>
          {services.map((s) => (
            <option key={s.id} value={s.serviceName}>{s.serviceName}</option>
          ))}
        </select>

        {/* Filter by Status / Profitability */}
        <select className="cm-select-filter" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Statuses</option>
          <option value="Profit">🟢 Profit (>=25%)</option>
          <option value="Low Profit">🟡 Low Profit (&lt;25%)</option>
          <option value="Loss">🔴 Loss (&lt;0)</option>
          <option value="Active">Active Clients Only</option>
          <option value="Inactive">Inactive Clients Only</option>
        </select>

        {/* Sort By */}
        <select className="cm-select-filter" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="profit_desc">Sort: Profit (High → Low)</option>
          <option value="profit_asc">Sort: Profit (Low → High)</option>
          <option value="margin_desc">Sort: Margin % (High → Low)</option>
          <option value="margin_asc">Sort: Margin % (Low → High)</option>
          <option value="income_desc">Sort: Income (High → Low)</option>
          <option value="expense_desc">Sort: Expense (High → Low)</option>
          <option value="name">Sort: Client Name</option>
        </select>

        {/* Export Buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="cm-btn-outline"
            style={{ background: 'var(--chip-bg)', color: 'var(--brand)', borderColor: '#dbeafe', fontSize: '0.85rem' }}
            onClick={exportCSV}
            title="Export data to Excel / CSV"
          >
            📊 Export Excel
          </button>
          <button
            type="button"
            className="cm-btn-outline"
            style={{ background: 'var(--chip-bg)', color: 'var(--brand)', borderColor: '#dbeafe', fontSize: '0.85rem' }}
            onClick={handlePrint}
            title="Print or Export PDF Report"
          >
            🖨️ Export PDF / Print
          </button>
        </div>

      </div>

      {/* Date Range Sub-Bar */}
      <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 16px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: '0.85rem' }}>
        <span style={{ fontWeight: 700, color: 'var(--muted)' }}>📅 Date Filter Range:</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>From:</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--line)' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>To:</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--line)' }} />
        </div>
        {(dateFrom || dateTo) && (
          <button
            type="button"
            style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => { setDateFrom(''); setDateTo(''); }}
          >
            Clear Dates
          </button>
        )}
      </div>

      {/* ── Client Profit Table ── */}
      {sortedClients.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--card)', borderRadius: 14, border: '2px dashed #e2e8f0', color: 'var(--muted)' }}>
          <IconChart size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--ink)' }}>No Client Analytics Found</div>
          <div style={{ fontSize: '0.85rem', marginTop: 4 }}>Try clearing search filters or add new income/expense entries.</div>
        </div>
      ) : (
        <div className="cm-table-card">
          <table className="cm-table">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Company Name</th>
                <th>Total Income</th>
                <th>Total Expense</th>
                <th>Gross Profit</th>
                <th>Profit Margin (%)</th>
                <th>Transactions</th>
                <th>Last Activity / Payment</th>
                <th>Profitability Health</th>
              </tr>
            </thead>
            <tbody>
              {sortedClients.map((c) => {
                const marginColor = c.grossProfit < 0 ? '#0f172a' : c.profitMarginPct < 25 ? '#0f172a' : '#224eae';
                return (
                  <tr
                    key={c.id}
                    onClick={() => onOpenClient && onOpenClient(c.id)}
                    style={{ cursor: 'pointer', transition: 'background .15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Client Name */}
                    <td>
                      <div style={{ fontWeight: 800, color: 'var(--ink)', fontSize: '0.9rem' }}>{c.clientName}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Status: {c.status}</div>
                    </td>

                    {/* Company Name */}
                    <td style={{ fontWeight: 600, color: 'var(--ink)' }}>
                      {c.company}
                    </td>

                    {/* Total Income */}
                    <td style={{ fontWeight: 800, color: 'var(--brand)' }}>
                      {money(c.totalIncome, currency)}
                    </td>

                    {/* Total Expense */}
                    <td style={{ fontWeight: 800, color: 'var(--ink)' }}>
                      {money(c.totalExpense, currency)}
                    </td>

                    {/* Gross Profit */}
                    <td style={{ fontWeight: 900, fontSize: '0.95rem', color: c.grossProfit >= 0 ? '#224eae' : '#0f172a' }}>
                      {money(c.grossProfit, currency)}
                    </td>

                    {/* Profit Margin (%) */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: 900, color: marginColor }}>
                          {c.profitMarginPct.toFixed(1)}%
                        </span>
                        <div style={{ width: 45, height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              width: `${Math.min(Math.max(c.profitMarginPct, 0), 100)}%`,
                              background: marginColor,
                              borderRadius: 99,
                            }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Transactions Count */}
                    <td style={{ fontWeight: 700, color: 'var(--muted)' }}>
                      <span className="chip" style={{ background: 'var(--bg)', color: 'var(--ink)' }}>
                        {c.totalTxnCount} txns
                      </span>
                    </td>

                    {/* Last Payment Date */}
                    <td style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
                      {c.lastPaymentDate ? fmtDDMMYYYY(c.lastPaymentDate) : '—'}
                    </td>

                    {/* Color Badges */}
                    <td>
                      {c.healthStatus === 'Profit' && (
                        <span style={{ background: 'var(--chip-bg)', color: 'var(--brand)', border: '1px solid var(--line)', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 800 }}>
                          🟢 Profit
                        </span>
                      )}
                      {c.healthStatus === 'Low Profit' && (
                        <span style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--line)', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 800 }}>
                          🟡 Low Profit
                        </span>
                      )}
                      {c.healthStatus === 'Loss' && (
                        <span style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--line)', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 800 }}>
                          🔴 Loss
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
